import {css, html, LitElement} from 'lit';
import {customElement, property, state} from 'lit/decorators';
import {containerStyles, normalizeStyles} from '@webs/components-library/theming';
import {MainMenuItem} from './main-menu-item';

@customElement('webs-navigation-menu')
export class NavigationMenu extends LitElement {

  static styles = [
    normalizeStyles,
    containerStyles,
    css`
      :host{
        display: flex;
        flex-direction: column;
      }

      .back-menu {
        margin-top: .5rem;
      }

      .main-menu {
        flex: 1;
        list-style: none;
        margin: 0;
        padding: 0;
        position: relative;
        width: 100%;
      }

      .main-menu a {
        align-items: baseline;
        color: var(--color-base);
        display: flex;
        font-family: var(--font-family-heading);
        font-size: 1rem;
        font-weight: var(--font-weight-base);
        justify-content: space-between;
        padding: .75rem 0;
        position: relative;
        text-decoration: none;
      }

      .main-menu a.highlighted span {
        position: relative;
      }

      webs-navigation-submenu {
        left: 0;
        position: absolute;
        top: 0;
        z-index: 999;
        transition: transform .1s linear;
      }

      webs-navigation-submenu.hidden {
        display: none;
        transform: translateX(100%);
      }

      @media only screen and (min-width: 1175px) {
        .main-menu {
          display: flex;
          position: unset;
          width: unset;
        }

        .main-menu li {
          display: block;
          margin: 0 .75rem;
        }

        .main-menu li.submenu-parent {
          position: relative;
        }

        .main-menu a {
          display: block;
          font-family: var(--font-family-alternative);
          font-size: .7rem;
          font-weight: var(--font-weight-semibold);
          padding: 1.6rem 0;
        }

        .main-menu a.highlighted span::after {
          background: linear-gradient(36.4deg, var(--color-orange) 22.12%, var(--color-pink) 110.96%);
          border-radius: 50%;
          content: '';
          height: 8px;
          left: calc(100% + 3px);
          position: absolute;
          top: 0;
          width: 8px;
        }

        .main-menu li:hover a::after {
          background: var(--color-base);
          content: '';
          height: 2px;
          left: 0;
          position: absolute;
          top: calc(100% - 1.6rem + .3rem);
          width: 100%;
        }

        .main-menu li.submenu-parent.align-right:last-child webs-navigation-submenu {
          left: auto;
          right: -2.3rem;
        }

        webs-navigation-submenu {
          left: -2.3rem;
          position: absolute;
          top: calc(100% + 1px);
          width: max-content;
          z-index: 999;
        }

        webs-navigation-submenu::before{
          bottom: 100%;
          content: '';
          height: 30px;
          left: 0;
          position: absolute;
          width: 100%;
        }

        webs-navigation-submenu.full-width {
          left: 50%;
          transform: translateX(-50%);
          width: 100vw;
        }
      }
    `
  ];

  private smallDevice: MediaQueryList = window.matchMedia('(min-width: 1175px)');
  @state()
  private isDesktop: boolean;
  private menuId: string;

  connectedCallback() {
    super.connectedCallback();
    this.smallDevice.addEventListener('change', (e) => this.handleDeviceChange(e));
  }

  @property({type: Array})
  items: MainMenuItem[];

  protected render(): unknown {
    this.handleDeviceChange(this.smallDevice);

    return html`
      ${this.items && this.items.length > 0 ? html`
        ${!this.isDesktop && this.menuId ? html`
          <a class="back-menu" @click="${this.hideSubmenu}">
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.64645 13.3536C6.84171 13.5488 7.15829 13.5488 7.35355 13.3536C7.54882 13.1583 7.54882 12.8417 7.35355 12.6464L6.64645 13.3536ZM0.999999 7L0.646446 6.64645C0.451184 6.84171 0.451184 7.15829 0.646446 7.35355L0.999999 7ZM7.35355 1.35355C7.54881 1.15829 7.54881 0.841709 7.35355 0.646446C7.15829 0.451184 6.84171 0.451184 6.64645 0.646446L7.35355 1.35355ZM7.35355 12.6464L1.35355 6.64645L0.646446 7.35355L6.64645 13.3536L7.35355 12.6464ZM1.35355 7.35355L7.35355 1.35355L6.64645 0.646446L0.646446 6.64645L1.35355 7.35355Z"
                fill="#181A23"/>
            </svg>
          </a>
        ` : ''}
        <ul class="main-menu">
          ${this.items.map((item, index) => {
            if (this.isDesktop) {
              const submenuParent = (item.submenuItems && item.submenuItems.length < 4) && !item.isFullWidth ? 'submenu-parent' : '';
              const alignRight = index == (this.items.length - 1) && item.submenuItems && item.submenuItems.length > 1 ? 'align-right' : '';
              return html`
                <li id="nav-main-${index}" class="${submenuParent} ${alignRight}"
                    @mouseover="${this.showSubmenu}" @mouseout="${this.hideSubmenu}">

                  <a href="${item.link ? item.link : 'javascript:;'}"
                     class="${this.hasTag(item) ? 'highlighted' : ''}"><span>${item.title}</span></a>

                  ${item.submenuItems ? html`
                    <webs-navigation-submenu
                      class="${this.menuId !== 'nav-main-' + index ? 'hidden' : ''} ${item.isFullWidth || item.submenuItems.length > 3 ? 'full-width' : ''}"
                      part="submenu" .items='${item.submenuItems}'>
                    </webs-navigation-submenu>` : ''}
                </li>
              `
            } else {
              return html`
                <li>
                  ${item.submenuItems && item.submenuItems.length > 0 ? html`
                    <a id="nav-main-${index}" href class="${this.hasTag(item) ? 'highlighted' : ''}" @click="${this.showSubmenu}">
                      <span>${item.title}</span>
                      <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M1.35355 0.646447C1.15829 0.451184 0.841709 0.451184 0.646447 0.646447C0.451184 0.841709 0.451184 1.15829 0.646447 1.35355L1.35355 0.646447ZM7 7L7.35355 7.35355C7.54882 7.15829 7.54882 6.84171 7.35355 6.64645L7 7ZM0.646447 12.6464C0.451184 12.8417 0.451184 13.1583 0.646447 13.3536C0.841709 13.5488 1.15829 13.5488 1.35355 13.3536L0.646447 12.6464ZM0.646447 1.35355L6.64645 7.35355L7.35355 6.64645L1.35355 0.646447L0.646447 1.35355ZM6.64645 6.64645L0.646447 12.6464L1.35355 13.3536L7.35355 7.35355L6.64645 6.64645Z"
                          fill="#181A23"/>
                      </svg>
                    </a>

                    <webs-navigation-submenu
                      class="${this.menuId !== 'nav-main-' + index ? 'hidden' : ''} ${item.isFullWidth || item.submenuItems.length > 3 ? 'full-width' : ''}"
                      part="submenu" .items='${item.submenuItems}'>
                    </webs-navigation-submenu>
                  ` : html`
                    <a href="${item.link}" class="${this.hasTag(item) ? 'highlighted' : ''}">
                      <span>${item.title}</span>
                    </a>
                  `}
                </li>`
            }
          })}
        </ul>
      ` : ''}
    `;
  }

  private showSubmenu(event: Event): void {
    event.preventDefault();
    this.menuId = (event.currentTarget as HTMLElement).id;
    this.requestUpdate();
  }

  private hideSubmenu(): void {
    this.menuId = '';
    this.requestUpdate();
  }

  private hasTag(item: MainMenuItem): boolean {
    if (!item.submenuItems) {
      return false;
    }
    const items = item.submenuItems.filter(value => value.items);

    for (const subItem of items) {
      const hasAnyTag = subItem.items.findIndex(inner => inner.tag !== undefined) > -1;
      if (hasAnyTag) {
        return true;
      }
    }

    return false;
  }

  private handleDeviceChange(event): void {
    this.isDesktop = event.matches;
    this.requestUpdate();
  }
}
